/*
 * Smartfits accessory upsells.
 *
 * Rendered by snippets/smartfits-addons.liquid (glass + pop holder) and
 * snippets/smartfits-straps.liquid (phone straps, one card per family with a
 * colour swatch per in-stock product). Both are <smartfits-addons> elements
 * and can sit on the same page.
 *
 *  - data-mode="pdp": pressing an accessory's "Προσθήκη" adds THAT accessory to
 *    the cart straight away as its own line (owner, 28 Sep 2026) and opens the
 *    drawer; the pill then reads "Στο καλάθι". The bundle price shows in the cart
 *    through the automatic "… με θήκη" discounts once a case is in the cart.
 *    The old "tick now, add with the case" path below only runs for boxes that
 *    were ticked but not yet added (normally none).
 *
 *  - data-mode="cart": each accessory has a one-click "+" button that adds it
 *    and re-renders the drawer.
 *
 * Swatches ([data-swatch]) inside a [data-strap-family] card switch the card's
 * image, colour name, price, links and the variant its checkbox / "+" adds.
 */
(function () {
  const ADD_URL = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

  const cartDrawer = () => document.querySelector('cart-drawer');

  function sectionsPayload(body) {
    const drawer = cartDrawer();
    if (!drawer || typeof drawer.getSectionsToRender !== 'function') return;
    body.append('sections', drawer.getSectionsToRender().map((s) => s.id).join(','));
    body.append('sections_url', window.location.pathname);
  }

  function renderCart(json) {
    const drawer = cartDrawer();
    if (drawer && json && json.sections) {
      drawer.renderContents(json);
      // renderContents only clears is-empty on .drawer__inner; this theme keeps
      // the class on <cart-drawer> itself, which would hide the items we just added
      drawer.classList.remove('is-empty');
      return;
    }
    // no drawer in this theme configuration: fall back to the cart page
    window.location = (window.routes && window.routes.cart_url) || '/cart';
  }

  // The screen protector has no model of its own, so the case's model travels
  // with it as a visible line-item property instead of being guessed later.
  function propertiesOf(el) {
    const name = el.dataset.propertyName;
    const value = el.dataset.propertyValue;
    return name && value ? { [name]: value } : {};
  }

  async function postItems(items) {
    const body = new FormData();
    items.forEach((item, i) => {
      body.append(`items[${i}][id]`, item.id);
      body.append(`items[${i}][quantity]`, item.quantity || 1);
      Object.keys(item.properties || {}).forEach((key) => {
        body.append(`items[${i}][properties][${key}]`, item.properties[key]);
      });
    });
    sectionsPayload(body);

    const res = await fetch(ADD_URL, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    const json = await res.json();
    if (json.status) throw new Error(json.description || json.message);
    return json;
  }

  /* ------------------------------------------------------ pdp submit */
  // One document-level listener for every panel on the page, so two panels
  // (accessories + straps) never post the cart twice.

  const pdpPanels = () =>
    Array.from(document.querySelectorAll('smartfits-addons[data-mode="pdp"]'));

  const allChecked = () =>
    pdpPanels().flatMap((panel) => panel.checked).filter((box) => !box.dataset.added);

  let submitHooked = false;

  function hookSubmit() {
    if (submitHooked) return;
    submitHooked = true;
    // Capture on the document so this runs before <product-form>'s own
    // submit listener and can take the request over when needed.
    document.addEventListener('submit', onSubmit, true);
  }

  function onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.type !== 'add-to-cart-form') return;
    // bundle deals / quantity breaks build their own items[] array
    if (document.querySelector('bundle-deals, quantity-breaks')) return;

    const productForm = form.closest('product-form');
    if (!productForm) return;
    const variantId = form.querySelector('[name="id"]');
    if (!variantId || !variantId.value) return;

    const checked = allChecked();
    if (!checked.length) return; // nothing ticked: leave the theme alone

    event.preventDefault();
    event.stopPropagation();
    submitWith(form, productForm, checked);
  }

  async function submitWith(form, productForm, checked) {
    const button = productForm.querySelector('[type="submit"]');
    const spinner = productForm.querySelector('.loading-overlay__spinner');
    // the sticky bar's button lives outside <product-form> but submits the same form
    const sticky = document.getElementById(`StickyAtcSubmitButton-${productForm.dataset.section}`);
    const errorWrapper = productForm.querySelector('.product-form__error-message-wrapper');
    const errorMessage = productForm.querySelector('.product-form__error-message');

    if (button) {
      button.setAttribute('aria-disabled', true);
      button.classList.add('loading');
    }
    if (spinner) spinner.classList.remove('hidden');
    if (sticky) {
      sticky.classList.add('loading');
      const stickySpinner = sticky.querySelector('.loading-overlay__spinner');
      if (stickySpinner) stickySpinner.classList.remove('hidden');
    }
    if (errorWrapper) errorWrapper.setAttribute('hidden', '');

    const data = new FormData(form);
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = value;
    }

    const items = [
      { id: data.get('id'), quantity: data.get('quantity') || 1, properties },
      ...checked.map((box) => ({
        id: box.dataset.variantId,
        quantity: 1,
        properties: propertiesOf(box),
      })),
    ];

    try {
      renderCart(await postItems(items));
      checked.forEach((box) => {
        box.checked = false;
        const row = box.closest('.sf-addon');
        if (row) row.classList.remove('sf-addon--checked');
      });
      pdpPanels().forEach((panel) => panel.syncTotal());
    } catch (error) {
      if (errorWrapper && errorMessage) {
        errorMessage.textContent = error.message;
        errorWrapper.removeAttribute('hidden');
      }
    } finally {
      if (button) {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
      }
      if (spinner) spinner.classList.add('hidden');
      if (sticky) {
        sticky.classList.remove('loading');
        const stickySpinner = sticky.querySelector('.loading-overlay__spinner');
        if (stickySpinner) stickySpinner.classList.add('hidden');
      }
    }
  }

  class SmartfitsAddons extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'pdp';
      this.totalEl = this.querySelector('[data-addons-total]');
      this.totalValueEl = this.querySelector('[data-addons-total-value]');

      this.addEventListener('click', this.onQuickviewClick.bind(this));
      this.addEventListener('click', this.onSwatchClick.bind(this));
      this.querySelectorAll('dialog.sf-quickview').forEach((dialog) => {
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
      });

      if (this.mode === 'cart') {
        this.addEventListener('click', this.onAddClick.bind(this));
        return;
      }

      this.addEventListener('change', this.onCheckboxChange.bind(this));
      hookSubmit();
      this.syncTotal();
    }

    /* -------------------------------------------------------- quickview */
    // Details/thumb open a same-page dialog instead of navigating to the
    // accessory's own product page. Its "add to cart" button just drives the
    // row's existing checkbox / add button, so the cart logic isn't duplicated.

    onQuickviewClick(event) {
      const opener = event.target.closest('[data-quickview-open]');
      if (opener) {
        // hand off to the theme's own quick-view modal, which loads the real
        // product page (native gallery, arrows and image zoom included)
        const row = opener.closest('.sf-addon');
        const native = row && row.querySelector('[data-native-qa]');
        if (native) {
          event.preventDefault();
          native.click();
        }
        return;
      }

      const closer = event.target.closest('[data-quickview-close]');
      if (closer) {
        const dialog = closer.closest('dialog');
        if (dialog) dialog.close();
        return;
      }

      const arrow = event.target.closest('[data-qv-prev], [data-qv-next]');
      if (arrow) {
        event.preventDefault();
        this.stepGallery(arrow.closest('[data-qv-gallery]'), arrow.hasAttribute('data-qv-next') ? 1 : -1);
        return;
      }

      const adder = event.target.closest('[data-quickview-add]');
      if (adder) {
        const dialog = adder.closest('dialog');
        const row = adder.closest('.sf-addon');
        const checkbox = row && row.querySelector('input[type="checkbox"][data-variant-id]');
        const addButton = row && row.querySelector('button.sf-addon__add[data-variant-id]');
        if (checkbox) {
          checkbox.checked = true;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (addButton) {
          addButton.click();
        }
        if (dialog) dialog.close();
      }
    }

    /* ---------------------------------------------------------- gallery */
    // The quickview shows every image of the product, not just the first one.

    galleryImages(gallery) {
      return (gallery && gallery.dataset.images ? gallery.dataset.images.split('|') : []).filter(Boolean);
    }

    stepGallery(gallery, delta) {
      const images = this.galleryImages(gallery);
      if (images.length < 2) return;
      const index = (Number(gallery.dataset.index || 0) + delta + images.length) % images.length;
      this.showGalleryImage(gallery, index);
    }

    showGalleryImage(gallery, index) {
      const images = this.galleryImages(gallery);
      const img = gallery.querySelector('[data-qv-img]');
      const count = gallery.querySelector('[data-qv-count]');
      gallery.dataset.index = String(index);
      if (img && images[index]) img.src = images[index];
      if (count) {
        count.textContent = `${index + 1}/${images.length}`;
        count.hidden = images.length < 2;
      }
      gallery.querySelectorAll('[data-qv-prev], [data-qv-next]').forEach((b) => { b.hidden = images.length < 2; });
    }

    /* ---------------------------------------------------------- swatches */
    // A strap family card holds one swatch per colour (each its own product).
    // Selecting one re-points the whole card — and its quickview — at that product.

    onSwatchClick(event) {
      const more = event.target.closest('[data-swatch-more]');
      if (more) {
        event.preventDefault();
        const row = more.closest('.sf-strap__swatches');
        const extras = row.querySelectorAll('[data-swatch-extra]');
        const expanded = more.getAttribute('aria-expanded') === 'true';
        extras.forEach((s) => { s.hidden = expanded; });
        more.setAttribute('aria-expanded', String(!expanded));
        more.textContent = expanded ? `+${extras.length}` : '−';
        return;
      }

      const swatch = event.target.closest('[data-swatch]');
      if (!swatch) return;
      event.preventDefault();
      const family = swatch.closest('[data-strap-family]');
      if (!family) return;
      const { variantId, price, priceText, priceWas, colour, url, image, imageLarge, images } = swatch.dataset;

      family.querySelectorAll('[data-swatch]').forEach((s) => {
        s.setAttribute('aria-pressed', String(s.dataset.variantId === variantId));
      });
      family.querySelectorAll('[data-strap-colour]').forEach((el) => (el.textContent = colour));
      family.querySelectorAll('[data-strap-price-now]').forEach((el) => (el.textContent = priceText));
      family.querySelectorAll('[data-strap-price-was]').forEach((el) => (el.textContent = priceWas || ''));
      family.querySelectorAll('[data-strap-price]').forEach((el) => {
        if (!el.querySelector('[data-strap-price-now]')) el.textContent = priceText;
      });
      family.querySelectorAll('[data-strap-link]').forEach((el) => (el.href = url));
      family.querySelectorAll('[data-native-qa]').forEach((el) => (el.dataset.productUrl = url));
      family.querySelectorAll('[data-strap-img]').forEach((el) => { if (image) el.src = image; });
      family.querySelectorAll('[data-strap-img-large]').forEach((el) => { if (imageLarge) el.src = imageLarge; });
      family.querySelectorAll('[data-qv-gallery]').forEach((gallery) => {
        gallery.dataset.images = images || imageLarge || '';
        this.showGalleryImage(gallery, 0);
      });

      const checkbox = family.querySelector('input[type="checkbox"][data-variant-id]');
      if (checkbox && checkbox.dataset.added && checkbox.dataset.variantId !== variantId) this.resetBox(checkbox);
      if (checkbox) {
        checkbox.dataset.variantId = variantId;
        checkbox.dataset.price = price;
        this.syncTotal();
      }
      const addButton = family.querySelector('button.sf-addon__add[data-variant-id]');
      if (addButton) addButton.dataset.variantId = variantId;
    }

    /* ------------------------------------------------------------- pdp */

    get boxes() {
      return Array.from(this.querySelectorAll('input[type="checkbox"][data-variant-id]'));
    }

    get checked() {
      return this.boxes.filter((b) => b.checked);
    }

    onCheckboxChange(event) {
      const box = event.target.closest('input[type="checkbox"][data-variant-id]');
      if (!box) return;
      if (box.dataset.added) { box.checked = true; return; } // already in the cart
      const row = box.closest('.sf-addon');
      if (row) row.classList.toggle('sf-addon--checked', box.checked);
      if (box.checked) this.addNow(box);
      this.syncTotal();
    }

    // "Προσθήκη" puts the accessory in the cart right away, as its own line.
    async addNow(box) {
      const row = box.closest('.sf-addon');
      const error = row && row.querySelector('.sf-addon__error');
      const text = row && row.querySelector('.sf-addon__box-text');
      if (error) error.hidden = true;
      box.disabled = true;
      if (row) row.classList.add('is-loading');
      try {
        renderCart(await postItems([{ id: box.dataset.variantId, quantity: 1, properties: propertiesOf(box) }]));
        box.dataset.added = '1';
        box.checked = true;
        if (text) text.textContent = 'Στο καλάθι';
      } catch (e) {
        box.checked = false;
        box.disabled = false;
        if (row) row.classList.remove('sf-addon--checked');
        if (error) { error.textContent = e.message; error.hidden = false; }
      } finally {
        if (row) row.classList.remove('is-loading');
        this.syncTotal();
      }
    }

    resetBox(box) {
      const row = box.closest('.sf-addon');
      const text = row && row.querySelector('.sf-addon__box-text');
      delete box.dataset.added;
      box.checked = false;
      box.disabled = false;
      if (row) row.classList.remove('sf-addon--checked');
      if (text) text.textContent = 'Προσθήκη';
    }

    syncTotal() {
      if (!this.totalEl) return;
      const checked = this.checked.filter((b) => !b.dataset.added);
      const cents = checked.reduce((sum, b) => sum + Number(b.dataset.price || 0), 0);
      this.totalEl.hidden = checked.length === 0;
      if (this.totalValueEl && checked.length) {
        this.totalValueEl.textContent = this.formatMoney(cents);
      }
    }

    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '{{amount}} €';
      const amount = (cents / 100).toFixed(2).replace('.', ',');
      return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    }

    /* ------------------------------------------------------------ cart */

    async onAddClick(event) {
      const button = event.target.closest('button[data-variant-id]');
      if (!button) return;
      event.preventDefault();

      const row = button.closest('.sf-addon');
      const error = row && row.querySelector('.sf-addon__error');
      if (error) error.hidden = true;
      button.disabled = true;
      button.classList.add('is-loading');

      try {
        renderCart(
          await postItems([
            { id: button.dataset.variantId, quantity: 1, properties: propertiesOf(button) },
          ])
        );
      } catch (e) {
        if (error) {
          error.textContent = e.message;
          error.hidden = false;
        }
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }
  }

  if (!customElements.get('smartfits-addons')) {
    customElements.define('smartfits-addons', SmartfitsAddons);
  }
})();
